/**
 * Normalizes any thrown value (axios error, network failure, etc.)
 * into a single predictable `ApiError`. Consumers (query hooks, UI)
 * should only ever deal with `ApiError`.
 */
import axios from 'axios'
import type { ApiErrorPayload } from './types'

export class ApiError extends Error {
  /** HTTP status code, or 0 for network/unknown errors. */
  readonly status: number
  /** Machine-readable error code from the API, if any. */
  readonly code?: string
  /** Per-field validation messages, if any. */
  readonly fieldErrors?: Record<string, Array<string>>
  /** Original error, kept for debugging. */
  readonly cause?: unknown

  constructor(params: {
    message: string
    status: number
    code?: string
    fieldErrors?: Record<string, Array<string>>
    cause?: unknown
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.fieldErrors = params.fieldErrors
    this.cause = params.cause
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isValidation(): boolean {
    return this.status === 422 || Boolean(this.fieldErrors)
  }
}

export function normalizeApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError<ApiErrorPayload>(error)) {
    const status = error.response?.status ?? 0
    const payload = error.response?.data
    return new ApiError({
      message: payload?.message || error.message || 'Request failed',
      status,
      code: payload?.code,
      fieldErrors: payload?.errors,
      cause: error,
    })
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message, status: 0, cause: error })
  }

  return new ApiError({ message: 'Unknown error', status: 0, cause: error })
}
